/** Then vs now: this year's messages against last year's, and the first thing you said this year. */

import { Avatar, Fit, Scene, Zone, type BlobSpec } from "../../story/stage";
import { useTimeline } from "../../story/timeline";
import type { Period, You } from "../../types";
import { count, lastDay, pct } from "../shared";

const BLOBS: BlobSpec[] = [{ colour: "blurple", x: 1030, y: 1820, r: 230, seed: 26.2, wob: 0.2 }];

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
const BAR = 936;

/** "NOV '25 – OCT '26", or the same months a year or more earlier. */
function span(period: Period, yearsBack: number) {
  const [sy, sm] = period.start.split("-").map(Number);
  const [ey, em] = lastDay(period.end).split("-").map(Number);
  const yy = (y: number) => String(y - yearsBack).slice(2);
  return `${MONTHS[sm - 1]} '${yy(sy)} – ${MONTHS[em - 1]} '${yy(ey)}`;
}

/** "1 Nov 2025 · 00:03", in the chat's own clock. */
function stamp(at: string) {
  const [y, m, d] = at.slice(0, 10).split("-").map(Number);
  const month = MONTHS[m - 1];
  return `${d} ${month[0]}${month.slice(1).toLowerCase()} ${y} · ${at.slice(11, 16)}`;
}

function verdict(change: number) {
  if (Math.abs(change) < 0.0005) return { big: "0%", sub: "the same number of messages as the year before." };
  return {
    big: `${change > 0 ? "+" : "−"}${pct(Math.abs(change))}`,
    sub: `${change > 0 ? "more" : "fewer"} messages than the year before.`,
  };
}

export function ThenVsNow({
  then,
  total,
  name,
  period,
}: {
  then: You["then_vs_now"];
  total: number;
  name: string;
  period: Period;
}) {
  const first = then.previous_total === null || then.change === null;
  const rows = [
    ...(first ? [] : [{ label: span(period, 1), n: then.previous_total ?? 0, colour: "var(--wr-ink-32)" }]),
    { label: span(period, 0), n: total, colour: "var(--wr-ink)" },
  ];
  const most = Math.max(1, ...rows.map((r) => r.n));
  const result = first ? null : verdict(then.change ?? 0);
  const message = then.first_message;

  const root = useTimeline((s) => {
    s.rise(["[data-part=kick]", "[data-part=lead]"], 0.35, { y: 30, stagger: 0.12 });
    s.q(".tv").forEach((row, i) => {
      const at = 0.55 + i * 0.25;
      s.rise(row, at, { y: 30, dur: 0.5, sound: false });
      s.tl.fromTo(row.querySelector(".tv-bar"), { scaleX: 0 }, { scaleX: 1, duration: 0.8, ease: "power3.out" }, at + 0.1);
      s.sound("zip", at + 0.1, 1 + i * 0.15, { length: 0.8, pan: -0.4 });
      s.count(row.querySelector("b"), Number(row.dataset.n), at + 0.1, 0.8);
    });
    s.tl.fromTo("[data-part=big]", { scale: 0.3, autoAlpha: 0, transformOrigin: "0% 60%" }, { scale: 1, autoAlpha: 1, duration: 0.6, ease: "back.out(2.4)" }, 1.65);
    s.sound("pop", 1.65, 1.3);
    s.rise("[data-part=sub]", 1.95, { y: 24, sound: false });
    s.rise("[data-part=first]", 2.5, { y: 0, sound: false });
    s.tl.fromTo(".bubble", { scale: 0.6, autoAlpha: 0, transformOrigin: "0% 100%" }, { scale: 1, autoAlpha: 1, duration: 0.5, ease: "back.out(2)" }, 2.65);
    s.sound("pop", 2.65, 1.4);
  });

  return (
    <div ref={root} className="contents">
      <Scene blobs={BLOBS}>
        <Zone at="top">
          <div data-part="kick" className="kick">
            Then vs now
          </div>
          <div data-part="lead" className="lead">
            Your year against the one before
          </div>
        </Zone>
        {/* The bars and the change are the moment: centred in whatever height there is. */}
        <Zone at="middle">
          {rows.map((row, i) => (
            <div key={row.label} className="tv" data-n={row.n} style={{ top: 400 + i * 200 }}>
              <div className="tv-h">
                <span>{row.label}</span>
                <b>{count(row.n)}</b>
              </div>
              <div className="tv-bar" style={{ width: Math.max(24, Math.round((row.n / most) * BAR)), background: row.colour }} />
            </div>
          ))}
          {result ? (
            <Fit size={300} className="abs disp" style={{ left: 60, right: 60, top: 800, color: "var(--wr-blurple)" }}>
              <span data-part="big" style={{ display: "inline-block" }}>
                {result.big}
              </span>
            </Fit>
          ) : (
            // No year before: one bar, and the big line says why.
            <div data-part="big" className="abs disp" style={{ left: 60, right: 60, top: 700, fontSize: 190, color: "var(--wr-blurple)" }}>
              Your first year here
            </div>
          )}
          <div data-part="sub" className="line" style={{ top: 1080 }}>
            {result ? result.sub : "There's no year before it to compare with."}
          </div>
        </Zone>
        {/* Your first message reads as the card's foot, so it keeps to the bottom. */}
        {message ? (
          <Zone at="bottom">
            <div data-part="first" className="kick" style={{ top: 1250 }}>
              Your first message this year
            </div>
            <div className="bubble" style={{ top: 1310 }}>
              <Avatar name={name} inline />
              <div style={{ minWidth: 0, overflowWrap: "anywhere" }}>
                <b>{name}</b>
                <time>{stamp(message.at)}</time>
                {/* A long first message stops at four lines rather than running off the card. */}
                <p style={{ display: "-webkit-box", WebkitLineClamp: 4, WebkitBoxOrient: "vertical", overflow: "hidden" }}>{message.text}</p>
              </div>
            </div>
          </Zone>
        ) : null}
      </Scene>
    </div>
  );
}
